import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
	Play,
	Loader2,
	CheckCircle,
	AlertCircle,
	Users,
	Camera,
	Clock,
	BarChart,
	Trophy,
	Target,
	Sparkles,
	Calendar,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import {
	runFullSimulation,
	getSimulationScenarios,
	runRealisticSimulation,
	getUserProfiles,
	type SimulationScenario,
	type RealisticSimulationResult,
} from "@/services/simulation";
import { getAdminOrganizations, getOrganizationUsers } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";

export function FullSimulation() {
	const { toast } = useToast();
	const [activeTab, setActiveTab] = useState<"realistic" | "basic">("realistic");

	// Basic simulation form
	const [formData, setFormData] = useState({
		organizationId: "",
		userId: "",
		days: 7,
		scenario: "productive-developer" as
			| "productive-developer"
			| "distracted-day"
			| "designer-workflow"
			| "meeting-heavy",
		dryRun: false,
	});

	// Realistic simulation form
	const [realisticForm, setRealisticForm] = useState({
		organizationId: "",
		duration: "2-weeks" as "1-week" | "2-weeks" | "1-month" | "custom",
		customDays: 14,
		teamComposition: "auto" as "auto" | "custom",
		includeWeekends: false,
		dryRun: false,
	});

	// Fetch organizations
	const { data: organizations } = useQuery({
		queryKey: ["organizations-list"],
		queryFn: () => getAdminOrganizations({ limit: 100 }),
	});

	// Fetch users for selected organization
	const { data: usersData } = useQuery({
		queryKey: ["org-users", formData.organizationId],
		queryFn: () => getOrganizationUsers(formData.organizationId, { limit: 100 }),
		enabled: !!formData.organizationId,
	});

	// Fetch scenarios
	const { data: scenarios } = useQuery({
		queryKey: ["simulation-scenarios"],
		queryFn: getSimulationScenarios,
	});

	// Fetch user profiles
	const { data: profiles } = useQuery({
		queryKey: ["simulation-profiles"],
		queryFn: getUserProfiles,
	});

	// Fetch users for realistic simulation
	const { data: realisticUsersData } = useQuery({
		queryKey: ["org-users-realistic", realisticForm.organizationId],
		queryFn: () => getOrganizationUsers(realisticForm.organizationId, { limit: 100 }),
		enabled: !!realisticForm.organizationId,
	});

	// Realistic simulation mutation
	const realisticMutation = useMutation({
		mutationFn: runRealisticSimulation,
		onSuccess: (data) => {
			toast({
				title: realisticForm.dryRun ? "Simulation Preview Complete" : "Realistic Simulation Complete",
				description: `Created ${data.sessionsCreated} sessions for ${data.usersSimulated} users over ${data.workingDays} working days`,
			});
		},
		onError: (error: any) => {
			toast({
				title: "Simulation Failed",
				description: error.message || "Failed to run simulation",
				variant: "destructive",
			});
		},
	});

	const simulationMutation = useMutation({
		mutationFn: runFullSimulation,
		onSuccess: (data) => {
			toast({
				title: formData.dryRun ? "Simulation Preview Complete" : "Simulation Complete",
				description: `Created ${data.sessionsCreated} sessions with avg score ${data.averageScore}`,
			});
		},
		onError: (error: any) => {
			toast({
				title: "Simulation Failed",
				description: error.message || "Failed to run simulation",
				variant: "destructive",
			});
		},
	});

	const handleSubmit = () => {
		if (!formData.organizationId) {
			toast({
				title: "Error",
				description: "Please select an organization",
				variant: "destructive",
			});
			return;
		}
		simulationMutation.mutate(formData);
	};

	const handleRealisticSubmit = () => {
		if (!realisticForm.organizationId) {
			toast({
				title: "Error",
				description: "Please select an organization",
				variant: "destructive",
			});
			return;
		}
		realisticMutation.mutate({
			organizationId: realisticForm.organizationId,
			duration: realisticForm.duration,
			customDays: realisticForm.duration === "custom" ? realisticForm.customDays : undefined,
			teamComposition: realisticForm.teamComposition,
			includeWeekends: realisticForm.includeWeekends,
			dryRun: realisticForm.dryRun,
		});
	};

	const result = simulationMutation.data;
	const realisticResult = realisticMutation.data as RealisticSimulationResult | undefined;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
						<Play className="w-6 h-6" />
						Full Pipeline Simulation
					</h1>
					<p className="text-gray-500 mt-1">
						Generate realistic monitoring data for testing and demos
					</p>
				</div>
			</div>

			{/* Tab Buttons */}
			<div className="flex gap-2 border-b">
				<button
					onClick={() => setActiveTab("realistic")}
					className={cn(
						"px-4 py-2 font-medium text-sm border-b-2 -mb-px transition-colors",
						activeTab === "realistic"
							? "border-blue-500 text-blue-600"
							: "border-transparent text-gray-500 hover:text-gray-700"
					)}
				>
					<Sparkles className="w-4 h-4 inline mr-2" />
					Realistic Simulation (Recommended)
				</button>
				<button
					onClick={() => setActiveTab("basic")}
					className={cn(
						"px-4 py-2 font-medium text-sm border-b-2 -mb-px transition-colors",
						activeTab === "basic"
							? "border-blue-500 text-blue-600"
							: "border-transparent text-gray-500 hover:text-gray-700"
					)}
				>
					<Play className="w-4 h-4 inline mr-2" />
					Basic Simulation
				</button>
			</div>

			{/* Realistic Simulation Tab */}
			{activeTab === "realistic" && (
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Sparkles className="w-5 h-5 text-purple-500" />
								Realistic Simulation
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-6">
							{/* Features Banner */}
							<div className="p-4 bg-purple-50 rounded-lg space-y-2">
								<p className="font-medium text-purple-900">Auto-generates realistic data:</p>
								<ul className="text-sm text-purple-700 space-y-1">
									<li>• Mixed user profiles (developers, designers, managers)</li>
									<li>• Daily productivity variation (±15%)</li>
									<li>• Weekday patterns (Monday warmup, Friday wind-down)</li>
									<li>• Skips weekends automatically</li>
								</ul>
							</div>

							{/* Organization */}
							<div>
								<Label>Organization</Label>
								<Select
									value={realisticForm.organizationId}
									onValueChange={(value) =>
										setRealisticForm({ ...realisticForm, organizationId: value })
									}
								>
									<SelectTrigger className="mt-2">
										<SelectValue placeholder="Select an organization" />
									</SelectTrigger>
									<SelectContent>
										{organizations?.organizations?.map((org) => (
											<SelectItem key={org._id} value={org._id}>
												{org.name} ({org.userCount || 0} users)
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{realisticForm.organizationId && realisticUsersData && (
									<p className="text-xs text-green-600 mt-1">
										{realisticUsersData.users.length} users will be simulated
									</p>
								)}
							</div>

							{/* Duration */}
							<div>
								<Label>Duration</Label>
								<div className="grid grid-cols-4 gap-2 mt-2">
									{[
										{ value: "1-week", label: "1 Week" },
										{ value: "2-weeks", label: "2 Weeks" },
										{ value: "1-month", label: "1 Month" },
										{ value: "custom", label: "Custom" },
									].map((option) => (
										<button
											key={option.value}
											type="button"
											onClick={() =>
												setRealisticForm({
													...realisticForm,
													duration: option.value as any,
												})
											}
											className={cn(
												"p-2 rounded-lg border-2 text-sm font-medium transition-colors",
												realisticForm.duration === option.value
													? "border-purple-500 bg-purple-50 text-purple-700"
													: "border-gray-200 hover:border-gray-300"
											)}
										>
											{option.label}
										</button>
									))}
								</div>
								{realisticForm.duration === "custom" && (
									<Input
										type="number"
										value={realisticForm.customDays}
										onChange={(e) =>
											setRealisticForm({
												...realisticForm,
												customDays: Math.min(90, Math.max(1, Number(e.target.value))),
											})
										}
										min={1}
										max={90}
										className="mt-2"
										placeholder="Number of days"
									/>
								)}
							</div>

							{/* Team Composition */}
							<div>
								<Label>Team Composition</Label>
								<div className="grid grid-cols-2 gap-2 mt-2">
									<button
										type="button"
										onClick={() =>
											setRealisticForm({ ...realisticForm, teamComposition: "auto" })
										}
										className={cn(
											"p-3 rounded-lg border-2 text-left transition-colors",
											realisticForm.teamComposition === "auto"
												? "border-purple-500 bg-purple-50"
												: "border-gray-200 hover:border-gray-300"
										)}
									>
										<p className="font-medium text-sm">Auto (Recommended)</p>
										<p className="text-xs text-gray-500 mt-1">
											Realistic mix of roles
										</p>
									</button>
									<button
										type="button"
										onClick={() =>
											setRealisticForm({ ...realisticForm, teamComposition: "custom" })
										}
										className={cn(
											"p-3 rounded-lg border-2 text-left transition-colors",
											realisticForm.teamComposition === "custom"
												? "border-purple-500 bg-purple-50"
												: "border-gray-200 hover:border-gray-300"
										)}
									>
										<p className="font-medium text-sm">Mixed Profile</p>
										<p className="text-xs text-gray-500 mt-1">
											All users same profile
										</p>
									</button>
								</div>
							</div>

							{/* Available Profiles Info */}
							{profiles && (
								<div>
									<Label>Profile Types (Auto-assigned)</Label>
									<div className="flex flex-wrap gap-2 mt-2">
										{profiles.map((profile) => (
											<Badge
												key={profile.id}
												variant="outline"
												className="text-xs"
											>
												{profile.name}: {profile.productivityRange[0]}-{profile.productivityRange[1]}%
											</Badge>
										))}
									</div>
								</div>
							)}

							{/* Include Weekends */}
							<div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
								<div>
									<Label>Include Weekends</Label>
									<p className="text-sm text-gray-500">
										Generate data for Saturday & Sunday
									</p>
								</div>
								<Switch
									checked={realisticForm.includeWeekends}
									onCheckedChange={(checked) =>
										setRealisticForm({ ...realisticForm, includeWeekends: checked })
									}
								/>
							</div>

							{/* Dry Run Toggle */}
							<div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
								<div>
									<Label className="text-amber-900">Dry Run Mode</Label>
									<p className="text-sm text-amber-700">
										Preview only, no database writes
									</p>
								</div>
								<Switch
									checked={realisticForm.dryRun}
									onCheckedChange={(checked) =>
										setRealisticForm({ ...realisticForm, dryRun: checked })
									}
								/>
							</div>

							{/* Warning */}
							{!realisticForm.dryRun && (
								<div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
									<AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
									<p>
										This will create real data. Consider using Dry Run first to preview.
									</p>
								</div>
							)}

							{/* Run Button */}
							<Button
								className="w-full bg-purple-600 hover:bg-purple-700"
								size="lg"
								onClick={handleRealisticSubmit}
								disabled={realisticMutation.isPending || !realisticForm.organizationId}
							>
								{realisticMutation.isPending ? (
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
								) : (
									<Sparkles className="w-4 h-4 mr-2" />
								)}
								{realisticForm.dryRun ? "Preview Realistic Simulation" : "Run Realistic Simulation"}
							</Button>
						</CardContent>
					</Card>

					{/* Realistic Results */}
					<Card>
						<CardHeader>
							<CardTitle>Results</CardTitle>
						</CardHeader>
						<CardContent>
							{realisticResult ? (
								<div className="space-y-6">
									{/* Summary */}
									<div className={cn(
										"p-6 rounded-lg text-center",
										realisticResult.errors.length > 0 ? "bg-amber-50" : "bg-green-50"
									)}>
										<CheckCircle
											className={cn(
												"w-12 h-12 mx-auto mb-3",
												realisticResult.errors.length > 0
													? "text-amber-500"
													: "text-green-500"
											)}
										/>
										<p className="font-medium text-lg">
											{realisticForm.dryRun ? "Simulation Preview" : "Simulation Complete"}
										</p>
										<p className="text-gray-600 mt-1">
											Avg Score: <span className="font-bold">{realisticResult.averageScore}%</span>
										</p>
									</div>

									{/* Date Range */}
									<div className="p-3 bg-blue-50 rounded-lg flex items-center justify-between">
										<div className="flex items-center gap-2">
											<Calendar className="w-4 h-4 text-blue-500" />
											<span className="text-sm text-blue-700">Date Range</span>
										</div>
										<span className="text-sm font-medium">
											{realisticResult.dateRange.start} to {realisticResult.dateRange.end}
										</span>
									</div>

									{/* Stats Grid */}
									<div className="grid grid-cols-2 gap-3">
										<div className="p-3 bg-gray-50 rounded-lg">
											<div className="flex items-center gap-2 mb-1">
												<Users className="w-4 h-4 text-blue-500" />
												<span className="text-xs text-gray-500">Users</span>
											</div>
											<p className="text-xl font-bold">{realisticResult.usersSimulated}</p>
										</div>
										<div className="p-3 bg-gray-50 rounded-lg">
											<div className="flex items-center gap-2 mb-1">
												<Calendar className="w-4 h-4 text-purple-500" />
												<span className="text-xs text-gray-500">Working Days</span>
											</div>
											<p className="text-xl font-bold">{realisticResult.workingDays}</p>
										</div>
										<div className="p-3 bg-gray-50 rounded-lg">
											<div className="flex items-center gap-2 mb-1">
												<Clock className="w-4 h-4 text-green-500" />
												<span className="text-xs text-gray-500">Sessions</span>
											</div>
											<p className="text-xl font-bold">{realisticResult.sessionsCreated}</p>
										</div>
										<div className="p-3 bg-gray-50 rounded-lg">
											<div className="flex items-center gap-2 mb-1">
												<Camera className="w-4 h-4 text-amber-500" />
												<span className="text-xs text-gray-500">Screenshots</span>
											</div>
											<p className="text-xl font-bold">{realisticResult.screenshotsCreated}</p>
										</div>
									</div>

									{/* User Breakdown */}
									{realisticResult.userBreakdown && realisticResult.userBreakdown.length > 0 && (
										<div>
											<h4 className="font-medium mb-2">User Breakdown</h4>
											<div className="space-y-2 max-h-48 overflow-y-auto">
												{realisticResult.userBreakdown.map((user) => (
													<div
														key={user.userId}
														className="flex items-center justify-between p-2 bg-gray-50 rounded"
													>
														<div>
															<p className="text-sm font-medium">{user.email}</p>
															<div className="flex items-center gap-1">
																<span className="text-xs text-gray-400">Profile:</span>
																<Badge variant="outline" className="text-xs">
																	{user.profile}
																</Badge>
															</div>
														</div>
														<div className="text-right">
															<p className="text-sm font-bold">{user.averageProductivity}%</p>
															<p className="text-xs text-gray-500">{user.sessionsCreated} sessions</p>
														</div>
													</div>
												))}
											</div>
										</div>
									)}

									{/* Errors */}
									{realisticResult.errors.length > 0 && (
										<div>
											<h4 className="font-medium text-red-700 mb-2">
												Errors ({realisticResult.errors.length})
											</h4>
											<div className="space-y-1 max-h-32 overflow-y-auto">
												{realisticResult.errors.slice(0, 5).map((error, index) => (
													<p
														key={index}
														className="text-sm text-red-600 p-2 bg-red-50 rounded"
													>
														{error}
													</p>
												))}
											</div>
										</div>
									)}
								</div>
							) : realisticMutation.isPending ? (
								<div className="text-center py-12">
									<Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-purple-500" />
									<p className="text-gray-600">Running realistic simulation...</p>
									<p className="text-sm text-gray-500 mt-1">
										This may take a while for longer durations
									</p>
								</div>
							) : (
								<div className="text-center py-12 text-gray-500">
									<Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
									<p>Configure and run a simulation to see results</p>
									<p className="text-sm mt-2">
										Recommended: 2 weeks with auto team composition
									</p>
								</div>
							)}
						</CardContent>
					</Card>
				</div>
			)}

			{/* Basic Simulation Tab */}
			{activeTab === "basic" && (
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Configuration */}
				<Card>
					<CardHeader>
						<CardTitle>Basic Configuration</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						{/* Organization */}
						<div>
							<Label>Organization</Label>
							<Select
								value={formData.organizationId}
								onValueChange={(value) =>
									setFormData({ ...formData, organizationId: value, userId: "" })
								}
							>
								<SelectTrigger className="mt-2">
									<SelectValue placeholder="Select an organization" />
								</SelectTrigger>
								<SelectContent>
									{organizations?.organizations?.map((org) => (
										<SelectItem key={org._id} value={org._id}>
											{org.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* User */}
						<div>
							<Label>User (optional)</Label>
							<Select
								value={formData.userId || "all"}
								onValueChange={(value) =>
									setFormData({ ...formData, userId: value === "all" ? "" : value })
								}
								disabled={!formData.organizationId}
							>
								<SelectTrigger className="mt-2">
									<SelectValue placeholder="All employees" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Employees</SelectItem>
									{usersData?.users?.map((user) => (
										<SelectItem key={user._id} value={user._id}>
											{user.email}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p className="text-xs text-gray-500 mt-1">
								Leave empty to simulate for all employees
							</p>
						</div>

						{/* Days */}
						<div>
							<Label>Days to Simulate</Label>
							<Input
								type="number"
								value={formData.days}
								onChange={(e) =>
									setFormData({
										...formData,
										days: Math.min(30, Math.max(1, Number(e.target.value))),
									})
								}
								min={1}
								max={30}
								className="mt-2"
							/>
							<p className="text-xs text-gray-500 mt-1">Maximum 30 days</p>
						</div>

						{/* Scenario */}
						<div>
							<Label>Scenario</Label>
							<div className="grid grid-cols-2 gap-3 mt-2">
								{(scenarios || []).map((scenario: SimulationScenario) => (
									<button
										key={scenario.id}
										type="button"
										onClick={() =>
											setFormData({
												...formData,
												scenario: scenario.id as any,
											})
										}
										className={cn(
											"p-3 rounded-lg border-2 text-left transition-colors",
											formData.scenario === scenario.id
												? "border-blue-500 bg-blue-50"
												: "border-gray-200 hover:border-gray-300"
										)}
									>
										<p className="font-medium text-sm">{scenario.name}</p>
										<p className="text-xs text-gray-500 mt-1 line-clamp-2">
											{scenario.description}
										</p>
									</button>
								))}
							</div>
						</div>

						{/* Dry Run Toggle */}
						<div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
							<div>
								<Label className="text-amber-900">Dry Run Mode</Label>
								<p className="text-sm text-amber-700">
									Preview only, no database writes
								</p>
							</div>
							<Switch
								checked={formData.dryRun}
								onCheckedChange={(checked) =>
									setFormData({ ...formData, dryRun: checked })
								}
							/>
						</div>

						{/* Warning */}
						{!formData.dryRun && (
							<div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
								<AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
								<p>
									This will create real data in the database. Use "Dry Run" mode
									to preview first, or use the Data Cleanup tool to remove
									simulation data.
								</p>
							</div>
						)}

						{/* Run Button */}
						<Button
							className="w-full"
							size="lg"
							onClick={handleSubmit}
							disabled={simulationMutation.isPending || !formData.organizationId}
						>
							{simulationMutation.isPending ? (
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							) : (
								<Play className="w-4 h-4 mr-2" />
							)}
							{formData.dryRun ? "Preview Simulation" : "Run Simulation"}
						</Button>
					</CardContent>
				</Card>

				{/* Results */}
				<Card>
					<CardHeader>
						<CardTitle>Results</CardTitle>
					</CardHeader>
					<CardContent>
						{result ? (
							<div className="space-y-6">
								{/* Summary */}
								<div className={cn(
									"p-6 rounded-lg text-center",
									result.errors.length > 0 ? "bg-amber-50" : "bg-green-50"
								)}>
									<CheckCircle
										className={cn(
											"w-12 h-12 mx-auto mb-3",
											result.errors.length > 0
												? "text-amber-500"
												: "text-green-500"
										)}
									/>
									<p className="font-medium text-lg">
										{formData.dryRun ? "Simulation Preview" : "Simulation Complete"}
									</p>
									{result.averageScore > 0 && (
										<p className="text-gray-600 mt-1">
											Average Score: <span className="font-bold">{result.averageScore}</span>
										</p>
									)}
								</div>

								{/* Stats Grid */}
								<div className="grid grid-cols-2 gap-3">
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<Users className="w-4 h-4 text-blue-500" />
											<span className="text-xs text-gray-500">Sessions</span>
										</div>
										<p className="text-xl font-bold">
											{result.sessionsCreated}
										</p>
									</div>
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<Camera className="w-4 h-4 text-purple-500" />
											<span className="text-xs text-gray-500">Screenshots</span>
										</div>
										<p className="text-xl font-bold">
											{result.screenshotsCreated}
										</p>
									</div>
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<Clock className="w-4 h-4 text-green-500" />
											<span className="text-xs text-gray-500">App Usage</span>
										</div>
										<p className="text-xl font-bold">
											{result.appUsageCreated}
										</p>
									</div>
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<BarChart className="w-4 h-4 text-amber-500" />
											<span className="text-xs text-gray-500">Summaries</span>
										</div>
										<p className="text-xl font-bold">
											{result.summariesGenerated}
										</p>
									</div>
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<Trophy className="w-4 h-4 text-orange-500" />
											<span className="text-xs text-gray-500">Gamification</span>
										</div>
										<p className="text-xl font-bold">
											{result.gamificationUpdates}
										</p>
									</div>
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<Target className="w-4 h-4 text-indigo-500" />
											<span className="text-xs text-gray-500">KPI Jobs</span>
										</div>
										<p className="text-xl font-bold">
											{result.kpiJobsQueued}
										</p>
									</div>
								</div>

								{/* Idle Events */}
								<div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
									<span className="text-sm text-gray-600">Idle Events Created</span>
									<Badge variant="outline">{result.idleEventsCreated}</Badge>
								</div>

								{/* Errors */}
								{result.errors.length > 0 && (
									<div>
										<h4 className="font-medium text-red-700 mb-2">
											Errors ({result.errors.length})
										</h4>
										<div className="space-y-1 max-h-32 overflow-y-auto">
											{result.errors.map((error, index) => (
												<p
													key={index}
													className="text-sm text-red-600 p-2 bg-red-50 rounded"
												>
													{error}
												</p>
											))}
										</div>
									</div>
								)}
							</div>
						) : simulationMutation.isPending ? (
							<div className="text-center py-12">
								<Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-500" />
								<p className="text-gray-600">Running simulation...</p>
								<p className="text-sm text-gray-500 mt-1">
									This may take a while for large datasets
								</p>
							</div>
						) : (
							<div className="text-center py-12 text-gray-500">
								<Play className="w-12 h-12 mx-auto mb-4 opacity-50" />
								<p>Configure and run a simulation to see results</p>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
			)}
		</div>
	);
}
